"use client";

import React, { useState, useEffect } from 'react';
import { Box, Spinner, Text } from '@chakra-ui/react';
import CardHeader from './CardHeader';
import CircularProgressChart from './CircularProgressChart';
import { metricsService, DealSentimentSummary } from '../../../services/metricsService';

interface DealSentimentCardProps {
  businessIdentifier?: string;
  timeRange?: string;
  value?: number;
  trend?: string;
  trendValue?: string;
  trendDirection?: 'up' | 'down';
}

const DealSentimentCard: React.FC<DealSentimentCardProps> = ({
  businessIdentifier,
  timeRange = '7d',
  value: propValue,
  trend: propTrend,
  trendValue: propTrendValue,
  trendDirection: propTrendDirection
}) => {
  const [sentimentData, setSentimentData] = useState<DealSentimentSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSentimentData = async () => {
      // Always try to fetch from API first, use props as fallback
      setIsLoading(true);
      setError(null);
      try {
        const data = await metricsService.getDealSentimentSummary(businessIdentifier, timeRange);
        setSentimentData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load deal sentiment data.');
        console.error('Error fetching sentiment data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSentimentData();
  }, [businessIdentifier, timeRange]);

  // Use API data if available, otherwise use prop values, otherwise use defaults
  // const value = sentimentData ? sentimentData.average_sentiment_score : (propValue !== undefined ? propValue : 70);
    const value = sentimentData && sentimentData.average_sentiment_score != null 
      ? sentimentData.average_sentiment_score.toFixed(2) 
      : "0.00";
  // Fix trend logic: use API data if sentimentData exists and has trend property (including 0)
  const trend = sentimentData && sentimentData.trend !== undefined ? 
    (sentimentData.trend === 0 ? 'No change' : sentimentData.trend > 0 ? 'Up by' : 'Down by') : 
    (propTrend || 'Down by');
    
  const trendValue = sentimentData && sentimentData.trend !== undefined ? 
    (sentimentData.trend === 0 ? '0%' : `${Math.abs(sentimentData.trend)}%`) : 
    (propTrendValue || '5%');
    
  const trendDirection: 'up' | 'down' = sentimentData && sentimentData.trend !== undefined ? 
    (sentimentData.trend >= 0 ? 'up' : 'down') : 
    (propTrendDirection || 'down');

  // Use red for negative trend, green for positive
  const secondaryColor = trendDirection === 'up' ? '#79FFB5' : '#FF7979';

  if (isLoading) {
    return (
      <Box
        bg="white"
        borderRadius="xl"
        shadow="sm"
        p={{ base: "3", md: "4" }}
        h={{ base: "auto", md: "350px" }}
        minH={{ base: "335px", md: "auto" }}
        w={{ base: "full", md: "370px" }}
        maxW={{ base: "370px", md: "full" }}
        mx={{ base: "auto", md: "0" }}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        bg="white"
        borderRadius="xl"
        shadow="sm"
        p={{ base: "3", md: "4" }}
        h={{ base: "auto", md: "350px" }}
        minH={{ base: "335px", md: "auto" }}
        w={{ base: "full", md: "370px" }}
        maxW={{ base: "370px", md: "full" }}
        mx={{ base: "auto", md: "0" }}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="red.500" fontSize="sm" textAlign="center">{error}</Text>
      </Box>
    );
  }
  
  return (
    <Box
      bg="white"
      borderRadius="xl"
      shadow="sm"
      p={{ base: "3", md: "4" }}
      h={{ base: "auto", md: "350px" }}
      minH={{ base: "335px", md: "auto" }}
      w={{ base: "full", md: "370px" }}
      maxW={{ base: "370px", md: "full" }}
      mx={{ base: "auto", md: "0" }}
      position="relative"
    >
      <CardHeader 
        title="Deal Sentiment" 
        trend={trend} 
        trendValue={trendValue} 
        trendDirection={trendDirection} 
      />
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        h="64" 
        mt={{ base: "5", md: "0" }}
      >
        <CircularProgressChart 
          value={value == null ? 0 : value} 
          primaryColor="#7987FF" 
          secondaryColor={secondaryColor} 
          trendValue={trendValue}
          trendDirection={trendDirection}
        />
      </Box>
    </Box>
  );
};

export default DealSentimentCard; 